import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { placeHolderImage } from '@/constants/placeHolderImage';
import { cn } from '@/lib/utils';
import {
  ConfigurableProduct,
  ConfigurableProductOptions,
  ConfigurableProductOptionsValues,
  ConfigurableVariant,
  Maybe,
} from '@/types/graphql.prod';
import { isProductConfigurable } from '@/util/isProductConfigurable';
import Image from 'next/image';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

export interface options_available_for_selection {
  __typename: string;
  option_value_uids: string[];
  attribute_code: string;
}

export const StandardOptions = ({
  configurableOptions,
  variants,
  product,
  handleClick,
  optionSpecs,
  onFieldChange,
  isVariantOnPresaleMap,
  variantInStockMap,
  existingOptionValues,
}: {
  configurableOptions: Maybe<Maybe<ConfigurableProductOptions>[]> | undefined;
  variants: Maybe<Maybe<ConfigurableVariant>[]> | undefined;
  product: ConfigurableProduct;
  handleClick: any;
  optionSpecs: string[];
  onFieldChange?: (attributeCode: string, value: any) => void;
  isVariantOnPresaleMap: Record<string, 0 | 1>;
  variantInStockMap: Record<string, 0 | 1>;
  existingOptionValues: Record<string, Set<string>>;
}) => {
  const form = useFormContext();
  const { watch } = form;
  const allFormData = watch();

  const packageQuantityBestValueMap = useMemo(
    () =>
      product?.variants?.reduce<any>((acc, cur) => {
        if (cur?.product?.best_value_mark !== 1) return acc;

        const packageQuantityValueIndex = cur?.attributes?.find(x => x?.code === 'package_quantity')?.value_index;

        if (packageQuantityValueIndex !== undefined && packageQuantityValueIndex !== null) {
          acc[packageQuantityValueIndex] = 1;
        }

        return acc;
      }, {}) || {},
    [product?.variants],
  );

  if (!isProductConfigurable(product)) {
    return null;
  }

  // configurableOptions 排序
  const sortedConfigurableOptions = configurableOptions?.slice(0)?.sort((a, b) => {
    return (a?.position ?? 0) - (b?.position ?? 0);
  });

  const isSingleAttribute = sortedConfigurableOptions?.length === 1;

  return (
    <div className='flex flex-col gap-4'>
      {sortedConfigurableOptions?.map((option, clickIndex) => {
        const shouldFilterOptionValues = 
          isSingleAttribute || option?.attribute_code === 'color';

        const filteredOptionValues = option?.values?.filter((value: any) => {
          if (!shouldFilterOptionValues) {
            return true;
          }
          const valueIndexStr = value?.value_index?.toString();
          const existingValues = existingOptionValues[option?.attribute_code ?? ''];
          return existingValues?.has(valueIndexStr);
        });

        const optionValues =
          option?.attribute_code === 'color'
            ? filteredOptionValues?.slice(0)?.sort((optionA, optionB) => {
                const optionAValueIndex = optionA?.value_index;
                const optionBValueIndex = optionB?.value_index;
                const isOptionAInStock = variantInStockMap[String(optionAValueIndex)] === 1;
                const isOptionBInStock = variantInStockMap[String(optionBValueIndex)] === 1;
                const isOptionAOnPresale = isVariantOnPresaleMap[String(optionAValueIndex)] === 1;
                const isOptionBOnPresale = isVariantOnPresaleMap[String(optionBValueIndex)] === 1;
                const isOptionASoldOut = !Object.prototype.hasOwnProperty.call(
                  variantInStockMap,
                  String(optionAValueIndex),
                );
                const isOptionBSoldOut = !Object.prototype.hasOwnProperty.call(
                  variantInStockMap,
                  String(optionBValueIndex),
                );
                const optionAOrder = isOptionASoldOut ? 4 : !isOptionAInStock ? 3 : isOptionAOnPresale ? 2 : 1;
                const optionBOrder = isOptionBSoldOut ? 4 : !isOptionBInStock ? 3 : isOptionBOnPresale ? 2 : 1;
                return optionAOrder - optionBOrder;
              })
            : filteredOptionValues;

        if (option?.attribute_code === 'color') {
          return (
            <FormField
              key={option?.uid}
              control={form.control}
              name={option?.attribute_code}
              render={({ field }) => (
                <FormItem className='space-y-3'>
                  <FormLabel className='text-4 block pb-2.5 font-poppins leading-[18px] text-title'>
                    <span className='font-[600]'>{option?.label}</span>:{' '}
                    <span className='option_selected__KhA8p font-oswald-bold text-base capitalize'>
                      {allFormData?.['color']?.label}
                    </span>
                  </FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={value => {
                        const selectedOption = option?.values?.find(v => v?.uid?.toString() === value);

                        if (selectedOption) {
                          field.onChange({
                            uid: value,
                            label: selectedOption?.default_label,
                            option_attribute_id: option.attribute_id,
                            option_selection_value: selectedOption?.value_index,
                          });

                          const shouldAddToSpec = optionSpecs.includes(selectedOption?.value_index?.toString() ?? '');

                          if (shouldAddToSpec) {
                            handleClick({
                              bool: true,
                              text: selectedOption?.value_index?.toString(),
                              index: clickIndex,
                            });
                          } else {
                            handleClick({
                              bool: true,
                              text: '',
                              index: clickIndex,
                            });
                          }

                          onFieldChange?.(option.attribute_code!, {
                            uid: value,
                            label: selectedOption?.default_label,
                            option_attribute_id: option.attribute_id,
                            option_selection_value: selectedOption?.value_index,
                          });
                        }
                      }}
                      value={field.value?.uid}
                      className='grid grid-cols-4 gap-x-[5px] gap-y-2.5 lg_flex lg_flex-wrap'
                    >
                      {optionValues?.map(value => (
                        <ColorOptionRadioItem
                          key={value?.uid}
                          item={value}
                          attributeCode={option?.attribute_code ?? ''}
                          variants={variants}
                          optionSpecs={optionSpecs}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage id='form-error' />
                </FormItem>
              )}
            />
          );
        }

        return (
          <FormField
            key={option?.uid}
            control={form.control}
            name={option?.attribute_code ?? ''}
            render={({ field }) => (
              <FormItem className='space-y-3'>
                <FormLabel className='text-4 block pb-2.5 font-poppins leading-[18px] text-title'>
                  <div className='flex items-center'>
                    <span>
                      <span className='font-[600]'>
                        {option?.label === 'Package Options' ? 'Package' : option?.label}
                      </span>
                      :{' '}
                      <span className='option_selected__KhA8p font-oswald-bold text-base capitalize'>
                        {allFormData?.[option?.attribute_code ?? '']?.label}
                      </span>
                    </span>
                  </div>
                </FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={value => {
                      const selectedOption = option?.values?.find(v => v?.uid?.toString() === value);

                      if (selectedOption) {
                        field.onChange({
                          uid: value,
                          label: selectedOption?.default_label,
                          option_attribute_id: option?.attribute_id,
                          option_selection_value: selectedOption?.value_index,
                        });

                        const shouldAddToSpec = optionSpecs.includes(selectedOption?.value_index?.toString() ?? '');

                        if (shouldAddToSpec) {
                          handleClick({
                            bool: true,
                            text: selectedOption?.value_index?.toString(),
                            index: clickIndex,
                          });
                        } else {
                          handleClick({
                            bool: true,
                            text: '',
                            index: clickIndex,
                          });
                        }

                        onFieldChange?.(option?.attribute_code!, {
                          uid: value,
                          label: selectedOption?.default_label,
                          option_attribute_id: option?.attribute_id,
                          option_selection_value: selectedOption?.value_index,
                        });

                        // handleColorLabelChange(selectedOption?.default_label?.toLowerCase())
                      }
                    }}
                    value={field.value?.uid}
                    className='flex flex-wrap gap-[15px]'
                  >
                    {optionValues?.map(value => (
                      <ConfigurableOptionRadioItem
                        key={value?.uid}
                        item={value}
                        isSize={option?.label === 'Size'}
                        optionSpecs={optionSpecs}
                        showBestValue={
                          option?.attribute_code === 'package_quantity' &&
                          packageQuantityBestValueMap[String(value?.value_index)] === 1
                        }
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage id='form-error' />
              </FormItem>
            )}
          />
        );
      })}
    </div>
  );
};

const ColorOptionRadioItem = ({
  item,
  attributeCode,
  variants,
  optionSpecs,
}: {
  item: Maybe<ConfigurableProductOptionsValues>;
  attributeCode: string;
  variants: Maybe<Maybe<ConfigurableVariant>[]> | undefined;
  optionSpecs: string[];
}) => {
  const couldSelect = optionSpecs.includes(item?.value_index?.toString() ?? '');
  const coudldNotSelect = !couldSelect;

  const optionImage = useMemo(() => {
    const find = variants?.find(temp => {
      return temp?.attributes?.some((attr: any) => attr.value_index === item?.value_index);
    });

    if (find) {
      const swatchImage = find.product?.swatch_image;

      if (swatchImage && swatchImage !== 'no_selection') {
        return `/media/catalog/product${swatchImage.indexOf('/') === 0 ? swatchImage : `/${swatchImage}`}`;
      }

      const hover_image = find?.product?.hover_image;
      const media_gallery = find?.product?.media_gallery?.sort((a, b) => {
        return (a?.position ?? 0) - (b?.position ?? 0);
      });

      const hoverImages = media_gallery?.filter(image => image?.url?.includes('hover'));

      if (hover_image) {
        return hover_image;
      } else if (hoverImages && hoverImages?.length > 0) {
        return hoverImages?.[0]?.url;
      }

      return media_gallery?.[1]?.url;
    }

    return '/logo_2.jpg';
  }, [variants, item?.value_index]);

  return (
    <FormItem className='space-y-0'>
      <FormControl>
        <RadioGroupItem
          value={item?.uid ?? ''}
          className='peer sr-only'
        />
      </FormControl>
      {
        <FormLabel
          className={cn(
            'flex cursor-pointer flex-col items-center border-[2px] border-solid border-[#ddd]',
            'overflow-hidden rounded-[10px] border-[2px] hover_border-primary peer-data-[state=checked]_border-primary',
            coudldNotSelect &&
              'border-[2px] border-transparent bg-[#f8f8f8] opacity-50 peer-data-[state=checked]_border-solid peer-data-[state=checked]_border-[#333]',
          )}
        >
          {attributeCode === 'color' && optionImage && (
            <Image
              src={optionImage}
              alt={item?.label ?? ''}
              width={100}
              height={100}
              className='h-full w-full lg_h-[100px] lg_w-[100px]'
              quality={95}
              placeholder={placeHolderImage}
            />
          )}
        </FormLabel>
      }
    </FormItem>
  );
};

const ConfigurableOptionRadioItem = ({
  item,
  isSize,
  optionSpecs,
  showBestValue = false,
}: {
  item: Maybe<ConfigurableProductOptionsValues>;
  isSize: boolean;
  optionSpecs: string[];
  showBestValue?: boolean;
}) => {
  const couldSelect = optionSpecs.includes(item?.value_index?.toString() ?? '');
  const coudldNotSelect = !couldSelect;

  return (
    <FormItem className='flex items-center space-x-0 space-y-0'>
      <FormControl>
        <RadioGroupItem
          value={item?.uid ?? ''}
          className='peer sr-only'
        />
      </FormControl>
      <FormLabel
        className={cn(
          'pointer-events-auto inline-flex h-[36px] min-w-[70px] cursor-pointer items-center justify-center rounded-sm  border-[2px] border-solid border-[#ddd] px-2xs py-0 text-center font-poppins text-sm font-[600] uppercase leading-none',
          'relative rounded-[10px] peer-data-[state=checked]_border-[2px] peer-data-[state=checked]_border-primary [&_has([data-state=checked])]_border-primary',
          coudldNotSelect && ' bg-[rgba(0,0,0,0.2)]',
          'relative',
        )}
      >
        {showBestValue && (
          <div className='absolute -right-0.5 -top-3 rounded-t-[5px] rounded-bl-[5px] bg-primary px-1 py-0.5 font-poppins text-[10px] font-semibold normal-case leading-[1.2] text-white'>
            Best Value
          </div>
        )}

        {coudldNotSelect && (
          <div className='absolute h-full w-full origin-top-left rotate-[24.23deg] transform border-t-1 border-solid border-[#666]' />
        )}
        <span className={cn('text-sm lg_text-[16px] ')}>
          {isSize ? item?.swatch_data?.value || item?.label : item?.label}
        </span>
      </FormLabel>
    </FormItem>
  );
};
